/**
 * Identity contracts added by workstream 08: the user directory (`UserDirectory` port, `/admin/directory/*`),
 * the role catalog the workbench uses to explain permissions, the sign-in providers list and the identity
 * event payloads. Additive; see docs/agent-briefs/STATUS/port-changes.md.
 */
import { z } from 'zod';
import { IsoDateTime } from './common.ts';

// ---------------------------------------------------------------------------------------------
// User directory
// ---------------------------------------------------------------------------------------------

/** Lowercase login name accepted by every directory adapter. */
export const DirectoryUsername = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9._-]+$/, 'lowercase letters, digits, dot, underscore and hyphen only');

/** A user as the bound `UserDirectory` reports it. `id` is the subject the IdP puts into `Principal.id`. */
export const DirectoryUser = z
  .object({
    id: z.string().min(1),
    username: z.string().min(1),
    displayName: z.string(),
    email: z.string().optional(),
    enabled: z.boolean(),
    roles: z.array(z.string()),
    /** Group names; they become `Principal.aclSubjects`. */
    groups: z.array(z.string()),
    /** Multivalued attributes, e.g. `domains` (task domains a requester may create tasks in). */
    attributes: z.record(z.string(), z.array(z.string())),
  })
  .meta({ id: 'DirectoryUser' });
export type DirectoryUser = z.infer<typeof DirectoryUser>;

/** What the bound directory adapter supports (the workbench hides what it cannot do). */
export const DirectoryCapabilities = z
  .object({ create: z.boolean(), setRoles: z.boolean(), disable: z.boolean(), invite: z.boolean() })
  .meta({ id: 'DirectoryCapabilities' });
export type DirectoryCapabilities = z.infer<typeof DirectoryCapabilities>;

export const DirectoryListQuery = z
  .object({
    search: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().max(200).optional(),
  })
  .meta({ id: 'DirectoryListQuery' });
export type DirectoryListQuery = z.infer<typeof DirectoryListQuery>;

export const DirectoryListResponse = z
  .object({
    items: z.array(DirectoryUser),
    cursor: z.string().optional(),
    /** Adapter serving the directory, e.g. `directory-local`. */
    adapter: z.string(),
    capabilities: DirectoryCapabilities,
  })
  .meta({ id: 'DirectoryListResponse' });
export type DirectoryListResponse = z.infer<typeof DirectoryListResponse>;

export const CreateDirectoryUserBody = z
  .object({
    username: DirectoryUsername,
    displayName: z.string().min(1).max(200),
    email: z.email().max(254).optional(),
    roles: z.array(z.string().min(1).max(64)).max(20).default([]),
    groups: z.array(z.string().min(1).max(100)).max(50).default([]),
    attributes: z.record(z.string().max(64), z.array(z.string().max(200)).max(50)).optional(),
    /** Set as a temporary credential: the user must change it at first sign-in. Never stored by the platform. */
    temporaryPassword: z.string().min(8).max(200).optional(),
    sendInvite: z.boolean().optional(),
  })
  .meta({ id: 'CreateDirectoryUserBody' });
export type CreateDirectoryUserBody = z.infer<typeof CreateDirectoryUserBody>;

export const SetDirectoryRolesBody = z
  .object({
    roles: z.array(z.string().min(1).max(64)).max(20),
    groups: z.array(z.string().min(1).max(100)).max(50),
  })
  .meta({ id: 'SetDirectoryRolesBody' });
export type SetDirectoryRolesBody = z.infer<typeof SetDirectoryRolesBody>;

export const SetDirectoryEnabledBody = z
  .object({ enabled: z.boolean(), reason: z.string().max(500).optional() })
  .meta({ id: 'SetDirectoryEnabledBody' });
export type SetDirectoryEnabledBody = z.infer<typeof SetDirectoryEnabledBody>;

// ---------------------------------------------------------------------------------------------
// Role catalog (mirrors core-intake ROLE_PERMISSIONS; a test in apps/platform keeps them equal)
// ---------------------------------------------------------------------------------------------

/** Human-readable permission names, keyed by the permission strings intake and approvals check. */
export const PERMISSION_LABELS: Readonly<Record<string, string>> = {
  'task.create': 'Create tasks',
  'task.view': 'View tasks',
  'task.cancel.own': 'Cancel own tasks',
  'task.cancel.any': 'Cancel any task',
  'task.clarify.own': 'Answer clarifications on own tasks',
  'task.clarify.any': 'Answer clarifications on any task',
  'approval.decide': 'Approve or reject actions',
  admin: 'Administer the platform (users, settings, stats)',
  registry: 'Change agents, tools and kill switches',
  chaos: 'Run chaos drills',
  // 11a registry bundles (agent studio, 12).
  'studio.draft': 'Draft and evaluate agent bundles',
  'studio.publish': 'Publish agent bundles to the registry',
  'studio.retire': 'Retire agent bundles',
  // document-delete: delete (retire, bytes removed) a case document someone else uploaded.
  'document.delete.any': 'Delete any case document',
};

export interface RoleCatalogEntry {
  /** One-line description shown next to the role checkbox. */
  description: string;
  permissions: readonly string[];
}

/** Platform roles a directory can grant, with what each allows. */
export const ROLE_CATALOG: Readonly<Record<string, RoleCatalogEntry>> = {
  requester: {
    description: 'Creates tasks in their domains; cancels and clarifies their own tasks.',
    permissions: ['task.create', 'task.view', 'task.cancel.own', 'task.clarify.own'],
  },
  approver: {
    description: 'Views tasks and approves or rejects actions (never their own requests).',
    permissions: ['task.view', 'approval.decide'],
  },
  csr: {
    description: 'Customer-service representative: creates customer-service tasks.',
    permissions: ['task.create', 'task.view', 'task.cancel.own', 'task.clarify.own'],
  },
  operator: {
    description: 'Operates the platform: any task, registry, kill switches, chaos drills, users.',
    permissions: [
      'task.create',
      'task.view',
      'task.cancel.any',
      'task.clarify.any',
      'admin',
      'registry',
      'chaos',
      'document.delete.any',
    ],
  },
  admin: {
    description: 'Everything an operator can do, and may grant the admin role.',
    permissions: [
      'task.create',
      'task.view',
      'task.cancel.any',
      'task.clarify.any',
      'admin',
      'registry',
      'chaos',
      // 11a: authoring, publishing and retiring agent bundles.
      'studio.draft',
      'studio.publish',
      'studio.retire',
      'document.delete.any',
    ],
  },
  // 11a: drafts and evaluates agent bundles; publishing needs `studio.publish` (admin).
  'agent-author': {
    description: 'Drafts and evaluates agent bundles in the agent studio; cannot publish or retire them.',
    permissions: ['studio.draft'],
  },
};

/** Roles of the catalog that grant `permission` (added by 11a). */
export function rolesWithPermission(permission: string): string[] {
  return Object.entries(ROLE_CATALOG)
    .filter(([, entry]) => entry.permissions.includes(permission))
    .map(([role]) => role);
}

/** Union of the permissions of `roles` (unknown roles grant nothing). */
export function permissionsForRoles(roles: readonly string[]): string[] {
  const out = new Set<string>();
  for (const role of roles) for (const p of ROLE_CATALOG[role]?.permissions ?? []) out.add(p);
  return [...out];
}

// ---------------------------------------------------------------------------------------------
// Sign-in providers (GET /auth/providers, served by idp-chain)
// ---------------------------------------------------------------------------------------------

export const AuthProviderView = z
  .object({
    name: z.string(),
    kind: z.enum(['oidc', 'local']),
    /** Browser URL that starts the login (`/auth/oidc/login` style), or the password login route for `local`. */
    loginUrl: z.string(),
    /** True for the break-glass local administrator login. */
    breakGlass: z.boolean().optional(),
    /** True when `POST /auth/login` accepts this provider's username and password (scripted demos only). */
    passwordLogin: z.boolean().optional(),
  })
  .meta({ id: 'AuthProviderView' });
export type AuthProviderView = z.infer<typeof AuthProviderView>;

export const AuthProvidersResponse = z
  .object({ providers: z.array(AuthProviderView) })
  .meta({ id: 'AuthProvidersResponse' });
export type AuthProvidersResponse = z.infer<typeof AuthProvidersResponse>;

// ---------------------------------------------------------------------------------------------
// Identity events
// ---------------------------------------------------------------------------------------------

/** Event types published by the identity adapters and `core-identity-admin`. */
export const IDENTITY_EVENT_TYPES = {
  login: 'identity.login',
  userCreated: 'identity.user.created',
  userRolesChanged: 'identity.user.roles.changed',
  userDisabled: 'identity.user.disabled',
  userEnabled: 'identity.user.enabled',
  /** Added by 21b: an external party was invited (or re-invited: token rotated). */
  externalInvited: 'identity.external.invited',
  /** Added by 21b: an external party was revoked (its token answers 401). */
  externalRevoked: 'identity.external.revoked',
} as const;

/** Audit entry kinds (`audit.entry` events) written for identity changes. */
export const IDENTITY_AUDIT_KINDS = {
  breakglassLogin: 'identity.breakglass.login',
  userCreated: 'identity.user.created',
  userRolesChanged: 'identity.user.roles.changed',
  userDisabled: 'identity.user.disabled',
  userEnabled: 'identity.user.enabled',
  /** Added by 21b. */
  externalInvited: 'identity.external.invited',
  /** Added by 21b. */
  externalRevoked: 'identity.external.revoked',
  /** Added by 21b (review fix 4): an operator/admin re-invited a revoked external. */
  externalReactivated: 'identity.external.reactivated',
} as const;

export const IdentityLoginData = z
  .object({
    subject: z.string(),
    username: z.string().optional(),
    provider: z.string(),
    breakGlass: z.boolean(),
    at: IsoDateTime,
  })
  .meta({ id: 'IdentityLoginData' });
export type IdentityLoginData = z.infer<typeof IdentityLoginData>;

export const IdentityUserChangedData = z
  .object({
    userId: z.string(),
    username: z.string(),
    by: z.string(),
    roles: z.array(z.string()).optional(),
    groups: z.array(z.string()).optional(),
    enabled: z.boolean().optional(),
    adapter: z.string(),
    at: IsoDateTime,
  })
  .meta({ id: 'IdentityUserChangedData' });
export type IdentityUserChangedData = z.infer<typeof IdentityUserChangedData>;

/** Added by 21b: `identity.external.invited` / `identity.external.revoked` (never carries the token). */
export const IdentityExternalChangedData = z
  .object({
    externalId: z.string(),
    email: z.string(),
    caseIds: z.array(z.string()),
    expiresAt: IsoDateTime,
    status: z.enum(['active', 'revoked']),
    by: z.string(),
    reinvited: z.boolean().optional(),
    at: IsoDateTime,
  })
  .meta({ id: 'IdentityExternalChangedData' });
export type IdentityExternalChangedData = z.infer<typeof IdentityExternalChangedData>;
